import { Routes } from '@angular/router';
import { environment } from '../environments/environment';
import { authGuard } from './core/guards/auth.guard';
import { AppShellComponent } from './core/layout/app-shell/app-shell.component';
import { ROUTE_SEGMENTS } from './core/routing/app-paths';
import { STORE_PLACEMENT } from './shared/app-store-links';
import type { OpenInAppRouteData } from './pages/open-in-app/open-in-app';

// ─────────────────────────────────────────────────────────────────────────────
// ONE domain, two surfaces:
//
//   • Marketing   — `/`, `/pricing`, `/for-owners`, legal & support pages. Always on.
//   • Web booking — `/complexes/...`, `/booking/...`, `/bookings`, `/events/...`, `/profile`,
//                   plus the auth screens. Mounted only when
//                   `environment.webBookingEnabled` is true, so the marketing site
//                   can ship independently of the product rollout.
//
// The product lives at the ROOT (no `/dashboard` prefix) on purpose: `/complexes/:id`,
// `/tournaments/:id` and `/join/:code` are the Universal Link / App Link paths claimed
// by the AASA + assetlinks (backend `DeepLinkPaths`). One URL therefore opens the app
// when it is installed and the real web page when it is not. Keep these paths in sync
// with the mobile GoRouter routes and `DeepLinkPaths` — never rename them here alone.
//
// While web booking is OFF, the deep-link paths fall back to `OpenInApp` (store
// redirect) exactly as before.
// ─────────────────────────────────────────────────────────────────────────────

const openInApp = () => import('./pages/open-in-app/open-in-app').then(m => m.OpenInApp);

// ── Marketing site (always mounted) ─────────────────────────────────────────
const marketingRoutes: Routes = [
  {
    path: '',
    loadComponent: () => import('./pages/landing/landing').then(m => m.Landing),
    title: 'SmartPlayr — Book Sports Courts & Find Arenas Near You',
  },
  {
    path: 'privacy',
    loadComponent: () => import('./pages/privacy/privacy').then(m => m.Privacy),
  },
  {
    path: 'terms',
    loadComponent: () => import('./pages/terms/terms').then(m => m.Terms),
    title: 'Terms of Service — SmartPlayr',
  },
  {
    path: 'partner-terms',
    loadComponent: () => import('./pages/partner-terms/partner-terms').then(m => m.PartnerTerms),
    title: 'Partner Terms — SmartPlayr for Venues',
  },
  {
    path: 'support',
    loadComponent: () => import('./pages/support/support').then(m => m.Support),
  },
  {
    path: 'delete-account',
    loadComponent: () => import('./pages/delete-account/delete-account').then(m => m.DeleteAccount),
  },
  {
    path: 'for-owners',
    loadComponent: () => import('./pages/for-owners/for-owners').then(m => m.ForOwners),
    title: 'List Your Facility — SmartPlayr for Owners',
  },
  {
    path: 'pricing',
    loadComponent: () => import('./pages/pricing/pricing').then(m => m.Pricing),
    title: 'Pricing — SmartPlayr for Venues',
  },
  {
    path: 'service-fee',
    loadComponent: () => import('./pages/service-fee/service-fee').then(m => m.ServiceFee),
    title: 'Service Fee — SmartPlayr',
  },
  // Short store link printed on venue posters (QR → smartplayr.com/app): phones are
  // redirected to their store, desktops get both buttons. Must NOT be claimed by the
  // AASA / assetlinks — the camera has to open it in the browser for the redirect to run.
  {
    path: 'app',
    loadComponent: openInApp,
    title: 'Get the SmartPlayr app',
    data: { placement: STORE_PLACEMENT.QR_POSTER, copy: 'GET_APP' } satisfies OpenInAppRouteData,
  },
];

// ── Deep-link fallbacks that stay app-only for now ──────────────────────────
// When the app is installed the OS intercepts these URLs and this page never loads;
// otherwise mobile visitors are sent to the store. Team invites are not part of the
// web product yet, so they keep the store fallback even when web booking is on.
// Move an entry into `productRoutes` when its web screen ships.
const appOnlyDeepLinkRoutes: Routes = [
  {
    path: `${ROUTE_SEGMENTS.join}/:code`,
    loadComponent: () => import('./pages/join/join').then(m => m.Join),
    title: 'Join a team — SmartPlayr',
  },
  {
    path: `${ROUTE_SEGMENTS.coachInvite}/:token`,
    loadComponent: () => import('./pages/coach-invite/coach-invite').then(m => m.CoachInvite),
    title: 'Coach invitation — SmartPlayr',
  },
];

// ── Deep-link fallbacks used ONLY while web booking is off ──────────────────
// Event emails/pushes link to `/events/{id}` (tournaments are events with the same id);
// `/tournaments/{id}` is kept only for links already sitting in inboxes.
const complexDeepLinkFallbackRoutes: Routes = [
  {
    path: `${ROUTE_SEGMENTS.complexes}/:id`,
    loadComponent: openInApp,
    title: 'Open in the SmartPlayr app',
  },
  {
    path: `${ROUTE_SEGMENTS.events}/:id`,
    loadComponent: openInApp,
    title: 'Open in the SmartPlayr app',
  },
  {
    path: `${ROUTE_SEGMENTS.tournaments}/:id`,
    loadComponent: openInApp,
    title: 'Open in the SmartPlayr app',
  },
];

// ── Web booking product (mounted when `webBookingEnabled`) ──────────────────
const productRoutes: Routes = [
  // Auth screens — root-level (`/login`, `/register`, `/verify-phone`, …), rendered in
  // AuthShellComponent (full-screen form on mobile, split-screen on desktop). The
  // guards redirect here by absolute path, so these must stay at the root.
  {
    path: '',
    loadChildren: () => import('./features/auth/auth.routes').then(m => m.authRoutes),
  },

  // Product shell — a PATHLESS parent: it gives the product screens the sidebar /
  // bottom-tab layout without adding a URL segment.
  {
    path: '',
    component: AppShellComponent,
    children: [
      // Browse + complex detail + reviews (public). `/complexes/:id` is the deep-link
      // URL: same path the app, the emails and the push notifications use.
      {
        path: ROUTE_SEGMENTS.complexes,
        loadChildren: () => import('./features/complexes/complexes.routes').then(m => m.complexesRoutes),
        title: 'Browse Courts — SmartPlayr',
      },
      // Booking checkout: confirm → payment → success (confirm/payment are auth-gated
      // inside bookingRoutes; the guard stores the return URL for post-login resume).
      {
        path: ROUTE_SEGMENTS.booking,
        loadChildren: () => import('./features/booking/booking.routes').then(m => m.bookingRoutes),
      },
      // My bookings (auth-gated). `/reservations` is the retired original path,
      // kept only as a redirect for bookmarks and stale links.
      {
        path: ROUTE_SEGMENTS.bookings,
        canActivate: [authGuard],
        loadChildren: () => import('./features/reservations/reservations.routes').then(m => m.reservationsRoutes),
        title: 'My Bookings — SmartPlayr',
      },
      { path: ROUTE_SEGMENTS.reservationsLegacy, redirectTo: ROUTE_SEGMENTS.bookings },
      { path: `${ROUTE_SEGMENTS.reservationsLegacy}/:id`, redirectTo: `${ROUTE_SEGMENTS.bookings}/:id` },
      // Events — browse and detail are public; register/payment guard themselves
      // inside eventsRoutes. `/events/:id` is the deep-link URL shared with the
      // app, the emails and the push notifications; `/tournaments/:id` is the same
      // event under its pre-merge name (same GUID), kept for links already sent.
      {
        path: ROUTE_SEGMENTS.events,
        loadChildren: () => import('./features/events/events.routes').then(m => m.eventsRoutes),
      },
      { path: `${ROUTE_SEGMENTS.tournaments}/:id`, redirectTo: `${ROUTE_SEGMENTS.events}/:id` },
      // Settings landing is public (language, theme, legal, sign-in CTA); account
      // screens guard themselves inside profileRoutes.
      {
        path: ROUTE_SEGMENTS.profile,
        loadChildren: () => import('./features/profile/profile.routes').then(m => m.profileRoutes),
        title: 'Settings — SmartPlayr',
      },

      // ── Not on web yet (screens exist under features/, kept app-only) ────────
      // teams, favorites, notifications. Mount them here when they ship on web,
      // and drop the matching entry from `appOnlyDeepLinkRoutes`.
    ],
  },
];

export const routes: Routes = [
  ...marketingRoutes,
  ...appOnlyDeepLinkRoutes,
  ...(environment.webBookingEnabled ? productRoutes : complexDeepLinkFallbackRoutes),

  // ── 404 — real page, keeps the URL, noindex (a redirect here would turn
  // every bad link into a soft 404 that crawlers index as the landing page) ──
  {
    path: '**',
    loadComponent: () => import('./pages/not-found/not-found').then(m => m.NotFound),
  },
];
